import type { FC, PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { NavigationBar, SidebarMenu } from "@components";
import { SidebarProvider } from "../../context/SidebarContext";
import { useUserSession } from "../../context/UserContext";
import { useOnboardingStatus } from "../../hooks";
import { PageContent, PageContentProps } from "./PageContent";
import { layoutPage } from "@design-system";

export const PageLayout: FC<PropsWithChildren<PageContentProps>> = function ({
  children,
  isFooter = true,
  fillViewport = false,
}) {
  const { userSession, isAuthenticated, authReady } = useUserSession();
  const { pathname } = useLocation();
  const onboarding = useOnboardingStatus();

  // Wait for auth to initialize to avoid premature redirects
  if (!authReady) {
    return (
      <div className={`min-h-screen ${layoutPage}`}>
        <div className="flex items-center justify-center h-screen">
          <div
            className="h-12 w-12 rounded-full border-4 border-gray-200 border-t-indigo-500 animate-spin"
            aria-label="Loading"
          />
        </div>
      </div>
    );
  }

  // Render app when authenticated
  if (userSession && isAuthenticated()) {
    // Gate the app behind initial setup: a workspace without a company is sent
    // to the wizard. We only redirect once the data has resolved and setup is
    // genuinely required, so a set-up workspace is never blocked while its data
    // is (re)loading. Everything beyond the company is guided in-app.
    if (
      !onboarding.isResolving &&
      onboarding.requiresInitialSetup &&
      pathname !== "/onboarding"
    ) {
      return <Navigate to="/onboarding" />;
    }

    return (
      <SidebarProvider>
        <NavigationBar {...userSession.user} />
        <div
          className={
            fillViewport
              ? "flex h-[100dvh] items-start"
              : "flex items-start h-screen"
          }
          style={{ paddingTop: "85px" }}
        >
          <SidebarMenu />
          <PageContent isFooter={isFooter} fillViewport={fillViewport}>
            {children}
          </PageContent>
        </div>
      </SidebarProvider>
    );
  }

  // Redirect to login page if user is not logged in (non-destructive)
  return <Navigate to="/login" />;
};
